import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useRouteMatch,
} from "react-router-dom";

import { LoadingSpinner } from "adminComponents/atoms/LoadingSpinner";
import { UpgradeCallToActionCard } from "adminComponents/atoms/UpgradeCallToActionCard";
import { ImproveWorkspaceUsageCard } from "adminComponents/molecules/ImproveWorkspaceUsageCard";
import { TopWorkspaceUsageCard } from "adminComponents/molecules/TopWorkspaceUsageCard";
import { AdminHomepage, AdminTab } from "adminComponents/screens/AdminHomepage";
import { useErrorToast } from "adminComponents/utils/toast";
import { usePageTrack } from "lib/contexts/analytics";
import { useAuth } from "lib/features/auth";
import {
  useFetchWorkspaceDomains,
  useFetchWorkspacePracticeMinutes,
  useFetchWorkspaceUsagePercent,
  useFetchWorkspaceUsers,
} from "lib/features/workspaces";
import { usePageTitle } from "lib/hooks/usePageTitle";
import { AnalyticsPage } from "lib/types";
import { AssignmentsTab } from "screens/AdminHome/components/AssignmentsTab";
import { SessionsTab } from "screens/AdminHome/components/SessionsTab";
import { useNavigationData } from "screens/TeacherDashboard/contexts/TeacherNavigationDataProvider";

import { StudentSnapshot } from "./components/StudentSnapshot";

const AdminHome: React.FC = () => {
  const { authUser, isFeatureEnabled, hasNoPremiumAccess } = useAuth();
  const { navigationData } = useNavigationData();
  const { t } = useTranslation("admin", {
    useSuspense: false,
  });
  const history = useHistory();
  const isSessionsTab = useRouteMatch("/a/reporting/sessions");
  const isAssignmentsTab = useRouteMatch("/a/reporting/assignments");

  usePageTitle(t("adminHomeContainer.pageTitle"));
  usePageTrack(AnalyticsPage.AdminDashboard_Home);

  const tabIndex = useMemo(() => {
    if (isSessionsTab) {
      return AdminTab.Sessions;
    }

    if (isAssignmentsTab) {
      return AdminTab.Assignments;
    }
  }, [isAssignmentsTab, isSessionsTab]);

  const handleTabChange = useCallback(
    (tab: AdminTab) => {
      switch (tab) {
        case AdminTab.Sessions:
          history.push(`/a/reporting/sessions`);
          return;
        case AdminTab.Assignments:
          history.push(`/a/reporting/assignments`);
          return;
      }
    },
    [history]
  );

  const practiceMinutesFetch = useFetchWorkspacePracticeMinutes({
    days_ago: 0,
    include_top_workspace_check: isFeatureEnabled(
      "playtime.enable_top_districts"
    ),
    refetchInterval: 60000,
  });
  const workspaceUsersFetch = useFetchWorkspaceUsers({ page: 1, perPage: 1 });
  const workspaceUsagePercentFetch = useFetchWorkspaceUsagePercent({
    enabled: isFeatureEnabled("playtime.enable_top_districts"),
  });
  const workspaceDomainsFetch = useFetchWorkspaceDomains();

  useErrorToast(practiceMinutesFetch.error);
  useErrorToast(workspaceUsersFetch.error);
  useErrorToast(workspaceUsagePercentFetch.error);
  useErrorToast(workspaceDomainsFetch.error);

  const isLoading = workspaceUsersFetch.isLoading;

  if (!authUser) {
    return <LoadingSpinner />;
  }

  let sideCard = undefined;

  if (isFeatureEnabled("playtime.enable_top_districts")) {
    sideCard = <ImproveWorkspaceUsageCard />;
    if (hasNoPremiumAccess) {
      sideCard = (
        <UpgradeCallToActionCard
          label={t("upgradeCTA.upgrade")}
          cta={t("upgradeCTA.upgrade")}
          upgradeText={t("districtAdminHomepage.usage.upgradeDescription")}
        />
      );
    } else if (
      workspaceUsagePercentFetch.data?.top_half_of_all_workspaces ||
      workspaceUsagePercentFetch.data?.top_third_of_all_workspaces
    ) {
      sideCard = (
        <TopWorkspaceUsageCard
          topThird={
            workspaceUsagePercentFetch.data?.top_third_of_all_workspaces
          }
        />
      );
    }
  }

  return (
    <AdminHomepage
      navigationData={navigationData}
      authUser={authUser}
      isLoading={isLoading}
      tabIndex={tabIndex}
      handleTabChange={handleTabChange}
      practiceMinutes={practiceMinutesFetch.data?.practice_minutes || 0}
      practiceMinutesIsLoading={practiceMinutesFetch.isLoading}
      topHalfPracticeMinutes={
        practiceMinutesFetch.data?.top_half_of_all_workspaces
      }
      totalWorkspaceUsers={workspaceUsersFetch.data?.total_count || 0}
      workspaceDomains={workspaceDomainsFetch.data?.workspace_domains}
    >
      <Switch>
        <Redirect from="/a/reporting" exact={true} to="/a/reporting/sessions" />
        <Route path="/a/reporting/sessions">
          <SessionsTab
            subjects={navigationData.subjects}
            gradeLevels={navigationData.gradeLevels}
            isLoading={workspaceUsagePercentFetch.isLoading}
            sideCard={sideCard}
          />
        </Route>
        <Route path="/a/reporting/assignments">
          <AssignmentsTab
            subjects={navigationData.subjects}
            gradeLevels={navigationData.gradeLevels}
            isLoading={workspaceUsagePercentFetch.isLoading}
            sideCard={sideCard}
          />
        </Route>
      </Switch>
      <StudentSnapshot />
    </AdminHomepage>
  );
};

export default AdminHome;
